import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import {
  ClipboardList, Loader2, CheckCircle2, Circle, CalendarClock, Flag, User, MessageSquare, Send, Forward, GitBranch, Hourglass, CheckCheck,
} from "lucide-react";
import { TaskChainDialog } from "./TaskChainDialog";
import { PortalForwardDialog } from "./PortalForwardDialog";
import { isOverdue, taskTiming } from "../lib/taskTiming";
import { TaskComments } from "./TaskComments";
import { SeenLine } from "./SeenLine";
import { TaskReportDialog, TaskApproveDialog, TaskReportView } from "./TaskReview";

const PRIO = {
  high: { ar: "عالية", cls: "bg-red-100 text-red-700 border-red-200" },
  medium: { ar: "متوسطة", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  low: { ar: "منخفضة", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export const PortalTasks = ({ botUsername = "", hasChatId = false, linkUrl = "" }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("open");
  const [commentTask, setCommentTask] = useState(null);
  const [chainTask, setChainTask] = useState(null);
  const [fwdTask, setFwdTask] = useState(null);
  const [canForward, setCanForward] = useState(false);
  const [reportTask, setReportTask] = useState(null);
  const [approveTask, setApproveTask] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.portalTasks(); setTasks(r.items || []); setCanForward(!!r.can_forward); }
    catch { toast.error("تعذّر تحميل المهام"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = (tk) => (tk.can_approve ? setApproveTask(tk) : tk.can_report ? setReportTask(tk) : null);

  const shown = useMemo(
    () => tasks.filter((t) => (filter === "all" ? true : filter === "overdue" ? isOverdue(t) : t.status === filter)),
    [tasks, filter]);
  const openCount = tasks.filter((t) => t.status === "open").length;
  const doneCount = tasks.filter((t) => t.status === "done").length;
  const reviewCount = tasks.filter((t) => t.status === "review").length;
  const overdueCount = tasks.filter(isOverdue).length;

  if (loading) return <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="portal-tasks">
      {botUsername && !hasChatId && (
        <a
          href={linkUrl || `https://t.me/${botUsername}?start=myid`}
          target="_blank"
          rel="noreferrer"
          data-testid="portal-request-chatid"
          className="flex items-start gap-2.5 rounded-2xl border border-sky-200 bg-sky-50 px-3.5 py-3 hover:bg-sky-100 transition-colors">
          <span className="h-9 w-9 rounded-xl bg-sky-600 grid place-items-center shrink-0">
            <Send className="h-4.5 w-4.5 text-white" />
          </span>
          <span className="text-right">
            <span className="block text-sm font-bold text-sky-900">فعّل إشعارات تيليجرام</span>
            <span className="block text-[11px] text-sky-700 leading-relaxed">
              {linkUrl
                ? "اضغط هنا لفتح بوت الشركة واضغط «ابدأ/Start» — سيتم تفعيل إشعارات المهام تلقائياً باسمك بدون نسخ أي رقم."
                : (<>اضغط هنا لفتح بوت الشركة وإرسال <code>/myid</code> — سيرسل لك رقمك المعرّف لتصلك المهام على تيليجرام. سلّمه لمديرك ليضيفه في ملفك.</>)}
            </span>
          </span>
        </a>
      )}

      <Tabs value={filter} onValueChange={setFilter} className="-mx-1 overflow-x-auto px-1 pb-1">
        <TabsList data-testid="portal-task-filter-tabs" className="w-max">
          <TabsTrigger value="open" data-testid="portal-task-filter-open">الجارية ({openCount})</TabsTrigger>
          <TabsTrigger value="review" data-testid="portal-task-filter-review">بانتظار الاعتماد ({reviewCount})</TabsTrigger>
          <TabsTrigger value="done" data-testid="portal-task-filter-done">المكتملة ({doneCount})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="portal-task-filter-overdue">لم تكتمل ({overdueCount})</TabsTrigger>
          <TabsTrigger value="all" data-testid="portal-task-filter-all">الكل ({tasks.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {shown.length === 0 ? (
        <div className="py-16 text-center text-slate-400" data-testid="portal-tasks-empty">
          <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-40" />
          لا توجد مهام مسندة إليك
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((tk) => {
            const done = tk.status === "done";
            const p = PRIO[tk.priority] || PRIO.medium;
            return (
              <motion.div key={tk.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`portal-task-${tk.id}`}
                className={`rounded-2xl border p-4 transition-colors ${done ? "bg-emerald-50/40 border-emerald-100" : "bg-white border-slate-200"}`}>
                <div className="flex items-start gap-3">
                  <button
                    data-testid={`portal-task-done-${tk.id}`}
                    disabled={done || !(tk.can_report || tk.can_approve) || busy === tk.id}
                    onClick={() => complete(tk)}
                    title={done ? "مكتملة ومعتمدة" : tk.can_approve ? "اعتماد وإغلاق" : tk.can_report ? "رفع تقرير الإنجاز" : "بانتظار اعتماد منشئ المهمة"}
                    className={`mt-0.5 shrink-0 transition ${!done && (tk.can_report || tk.can_approve) ? "hover:scale-110" : "cursor-default"}`}>
                    {busy === tk.id ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                      : done ? <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                        : tk.status === "review" ? <Hourglass className="h-6 w-6 text-violet-500" />
                          : <Circle className="h-6 w-6 text-slate-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <button type="button" onClick={() => setCommentTask(tk)} data-testid={`portal-task-open-${tk.id}`} className={`text-right font-heading font-bold text-slate-900 hover:text-blue-700 ${done ? "line-through text-slate-400" : ""}`}>{tk.title}</button>
                    {tk.description && <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap">{tk.description}</p>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <Badge variant="outline" className={`gap-1 text-[11px] ${p.cls}`}><Flag className="h-3 w-3" />{p.ar}</Badge>
                      {tk.due_date && <Badge variant="outline" className="gap-1 text-[11px]"><CalendarClock className="h-3 w-3" />{tk.due_date}</Badge>}
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${taskTiming(tk)[1]}`} data-testid={`portal-task-timing-${tk.id}`}>{taskTiming(tk)[0]}</span>
                      {tk.status === "review" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700" data-testid={`portal-task-review-badge-${tk.id}`}>بانتظار الاعتماد</span>}
                      {tk.sub_total ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700">محوّلة {tk.sub_done || 0}/{tk.sub_total}</span> : null}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-1">
                      <User className="h-3 w-3" /> بواسطة {tk.created_by_name || "—"}
                      {done && tk.completed_by_name && <span className="text-emerald-600 ms-auto">✓ تم الإنجاز والاعتماد</span>}
                    </div>
                    <TaskReportView task={tk} mode="portal" />
                    <SeenLine items={tk.seen_by} testId={`portal-task-seen-${tk.id}`} />
                    {tk.can_report && (
                      <button data-testid={`portal-task-report-btn-${tk.id}`} onClick={() => setReportTask(tk)}
                        className="mt-2 me-3 inline-flex items-center gap-1 rounded-lg bg-blue-600 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-blue-700"><Send className="h-3.5 w-3.5" /> رفع تقرير الإنجاز</button>)}
                    {tk.can_approve && (
                      <button data-testid={`portal-task-approve-open-${tk.id}`} onClick={() => setApproveTask(tk)}
                        className="mt-2 me-3 inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-emerald-700"><CheckCheck className="h-3.5 w-3.5" /> {tk.status === "review" ? "مراجعة التقرير والاعتماد" : "إغلاق وتقييم"}</button>)}
                    <button data-testid={`portal-task-comments-${tk.id}`} onClick={() => setCommentTask(tk)}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-blue-600 transition">
                      <MessageSquare className="h-3.5 w-3.5" /> تعليقات{tk.comment_count ? ` (${tk.comment_count})` : ""}
                    </button>
                    {(tk.parent_id || tk.sub_total) ? (
                      <button data-testid={`portal-task-chain-${tk.id}`} onClick={() => setChainTask(tk)}
                        className="mt-2 ms-3 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-indigo-600"><GitBranch className="h-3.5 w-3.5" /> التسلسل</button>) : null}
                    {canForward && !done && (
                      <button data-testid={`portal-task-forward-${tk.id}`} onClick={() => setFwdTask(tk)}
                        className="mt-2 ms-3 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-blue-600"><Forward className="h-3.5 w-3.5" /> تحويل المهمة</button>)}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      <TaskChainDialog task={chainTask} mode="portal" onClose={() => setChainTask(null)} />
      <TaskReportDialog task={reportTask} mode="portal" onClose={() => setReportTask(null)} onDone={load} />
      <TaskApproveDialog task={approveTask} mode="portal" onClose={() => setApproveTask(null)} onDone={load} />
      <PortalForwardDialog task={fwdTask} onClose={() => setFwdTask(null)} onDone={load} />
      <TaskComments task={commentTask} mode="portal" onClose={() => setCommentTask(null)} onPosted={load} />
    </div>
  );
};
